<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Label from 'dashboard/components-next/label/Label.vue';
import LabelPill from 'dashboard/components-next/label/LabelPill.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  title: { type: String, required: true },
  description: { type: String, default: '' },
  href: { type: String, default: '' },
  bgColor: { type: String, default: '' },
  small: { type: Boolean, default: false },
  showClose: { type: Boolean, default: false },
  icon: { type: String, default: '' },
  color: { type: String, default: '' },
  emoji: { type: String, default: '' },
  colorScheme: { type: String, default: '' },
  variant: { type: String, default: '' },
});
const emit = defineEmits(['remove']);
const { t } = useI18n();
const isListPill = computed(
  () =>
    ['smooth', 'dashed'].includes(props.variant) && props.color && !props.href
);
const label = computed(() => ({
  title: props.title,
  description: props.description,
  color: props.color || props.bgColor,
  emoji: props.emoji,
}));
const scheme = computed(
  () =>
    ({
      primary: 'blue',
      secondary: 'slate',
      success: 'teal',
      alert: 'ruby',
      warning: 'amber',
    })[props.colorScheme] || 'slate'
);
</script>

<template>
  <div
    class="mb-1 me-1 inline-flex max-w-full min-w-0 items-center gap-0.5"
    data-conversation-label-chip
    :title="description"
  >
    <template v-if="isListPill">
      <fluent-icon v-if="icon" :icon="icon" :size="12" class="shrink-0" />
      <LabelPill
        :label="label"
        :dense="small"
        :dashed-outline="variant === 'dashed'"
      />
    </template>
    <Label
      v-else
      :label="{ ...label, color: icon ? '' : label.color }"
      :compact="small"
      :color="scheme"
      class="!min-w-0 !max-w-full !shrink"
    >
      <template #icon>
        <fluent-icon v-if="icon" :icon="icon" :size="12" class="shrink-0" />
      </template>
      <template #label>
        <span v-if="emoji" class="shrink-0 leading-none" aria-hidden="true">{{
          emoji
        }}</span>
        <a
          v-if="href"
          :href="href"
          class="min-w-0 truncate text-inherit hover:underline focus-visible:underline"
          >{{ title }}</a
        >
        <span v-else class="min-w-0 truncate">{{ title }}</span>
      </template>
    </Label>
    <Button
      v-if="showClose"
      type="button"
      size="xs"
      icon="i-lucide-x"
      variant="ghost"
      color="slate"
      :aria-label="`${t('GENERAL.CLOSE')} ${title}`"
      @click="emit('remove', title)"
    />
  </div>
</template>
